import { Box, Flex, Text } from '@chakra-ui/react';
import { Container, DisplayHeading, DotList, Eyebrow, Stamp } from '@/travo/components/ui/postmark';
import { useCopy } from '@/travo/i18n';
import { HARD_SHADOW, PILL_BUTTON } from '@/travo/theme/constants';

const PlanCopy = () => {
  const { plan } = useCopy();

  return (
    <Flex direction="column" gap="24px" flex="1 1 420px" minW={0}>
      <Eyebrow>{plan.eyebrow}</Eyebrow>
      <DisplayHeading fontSize={{ base: '36px', md: '54px' }}>{plan.title}</DisplayHeading>
      <Text color="inkSoft" maxW="30em">
        {plan.body}
      </Text>
      <DotList items={plan.points} dot="stamp" />
    </Flex>
  );
};

/** Home-screen itinerary widget mock — illustrative times. */
const ItineraryWidget = () => {
  const { widget } = useCopy().plan;

  return (
    <Flex
      direction="column"
      gap="14px"
      w="100%"
      maxW="380px"
      bg="ink"
      color="paper"
      borderRadius="28px"
      px="24px"
      py="22px"
      boxShadow={HARD_SHADOW.card}
      transform="rotate(-2deg)"
    >
      <Flex justify="space-between" fontFamily="mono" fontSize="12px" letterSpacing="0.14em" color="sky">
        <Text>{widget.day}</Text>
        <Text>{widget.label}</Text>
      </Flex>
      <Box>
        <Text fontFamily="mono" fontSize="11px" letterSpacing="0.16em" color="sun">
          {widget.nowLabel}
        </Text>
        <Text fontSize="22px" fontWeight="700" lineHeight="1.3">
          {widget.now}
        </Text>
      </Box>
      <Box borderTop="1px solid" borderColor="paper/20" pt="12px">
        <Text fontFamily="mono" fontSize="11px" letterSpacing="0.16em" color="sky">
          {widget.nextLabel}
        </Text>
        <Text fontSize="17px" fontWeight="500">
          {widget.next}
        </Text>
      </Box>
      <Flex justify="space-between" fontSize="14px" color="sand">
        <Text>{widget.later}</Text>
        <Text>{widget.more}</Text>
      </Flex>
    </Flex>
  );
};

/** A published Discover plan, as a stamp. */
const DiscoverCard = () => {
  const { discover } = useCopy().plan;

  return (
    <Stamp w="100%" maxW="400px" margin="14px" innerPadding="18px" transform="rotate(2deg)">
      <Flex direction="column" gap="10px">
        <Flex justify="space-between" fontFamily="mono" fontSize="11px" letterSpacing="0.14em">
          <Text>{discover.kicker}</Text>
          <Text>{discover.length}</Text>
        </Flex>
        <Text fontSize="21px" fontWeight="700" lineHeight="1.3">
          {discover.title}
        </Text>
        <Text fontSize="14px" color="muted">
          {discover.byline}
        </Text>
        {/* Visual only: the real action lives in the app */}
        <Box as="span" {...PILL_BUTTON} alignSelf="flex-start" fontSize="15px" px="18px" py="9px" cursor="default" aria-hidden>
          {discover.cta}
        </Box>
      </Flex>
    </Stamp>
  );
};

/** Itinerary + Discover: a home-screen widget mock and a Discover plan card. */
const PlanTrip = () => (
  <Box as="section" id="plan" bg="sand" borderY="2px solid" borderColor="ink">
    <Container py={{ base: '80px', md: '104px' }}>
      <Flex wrap="wrap-reverse" align="center" gap="64px">
        <PlanCopy />
        <Flex direction="column" gap="24px" align="center" flex="1 1 460px" minW={0}>
          <ItineraryWidget />
          <DiscoverCard />
        </Flex>
      </Flex>
    </Container>
  </Box>
);

export default PlanTrip;
